import React, { useRef } from "react";
import ReactPaginate from "react-paginate";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaCog,
  FaEnvelope,
  FaGlobe,
  FaListAlt,
  FaMapMarkerAlt,
  FaPhone,
  FaUser,
  FaUserCheck,
} from "react-icons/fa";
import { CMS_PRIMARY } from "../TaskManagement/DailyActivityLogger/DailyActivityLoggerConstants";

export const formatFormType = (type) => {
  if (type === "registration" || type === "Registration") return "Study Abroad";
  if (type === "immigration" || type === "Immigration") return "Immigration (Visa)";
  return type || "—";
};

export const FORM_TYPE_BADGE_CLASS = {
  study: "website-leads-badge website-leads-badge--study",
  visa: "website-leads-badge website-leads-badge--visa",
  default: "website-leads-badge website-leads-badge--default",
};

export const getFormTypeBadgeClass = (type) => {
  if (type === "registration" || type === "Registration") {
    return FORM_TYPE_BADGE_CLASS.study;
  }
  if (type === "immigration" || type === "Immigration") {
    return FORM_TYPE_BADGE_CLASS.visa;
  }
  return FORM_TYPE_BADGE_CLASS.default;
};

/** @deprecated use getFormTypeBadgeClass */
export const FORM_TYPE_STYLES = FORM_TYPE_BADGE_CLASS;

/** @deprecated use getFormTypeBadgeClass */
export const getFormTypeStyle = getFormTypeBadgeClass;

export function LeadsFormTypeBadge({ type, size = "sm" }) {
  return (
    <span className={`${getFormTypeBadgeClass(type)} website-leads-badge--${size}`}>
      {formatFormType(type)}
    </span>
  );
}

export function LeadsAssignedBadge({ name }) {
  const assigned = name && name !== "—";
  return (
    <span
      className={`website-leads-badge website-leads-badge--sm ${
        assigned
          ? "website-leads-badge--assigned"
          : "website-leads-badge--unassigned"
      }`}
      title={assigned ? name : "Unassigned"}
    >
      {assigned ? name : "Unassigned"}
    </span>
  );
}

export const formatLeadDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export function LeadsPageShell({ children }) {
  return (
    <div className="tailwind-container">
      <div className="website-leads-page">{children}</div>
    </div>
  );
}

export function LeadsPageHeader({ icon: Icon, title, subtitle, actions }) {
  return (
    <div className="website-leads-page-header">
      <div className="website-leads-page-header__top">
        <div className="website-leads-page-header__main">
          <div
            className="d-flex align-items-center justify-content-center rounded-lg"
            style={{
              width: 36,
              height: 36,
              backgroundColor: "rgba(0,123,255,0.1)",
              flexShrink: 0,
            }}
          >
            <Icon size={16} style={{ color: CMS_PRIMARY }} />
          </div>
          <div className="min-w-0">
            <h1
              className="mb-0 text-truncate"
              style={{
                fontSize: "1.05rem",
                fontWeight: 700,
                color: "#35314d",
              }}
            >
              {title}
            </h1>
            {subtitle && (
              <p className="mb-0 text-truncate" style={{ fontSize: "11px", color: "#9ca3af" }}>
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {actions && (
          <div className="d-flex flex-wrap align-items-center gap-2">{actions}</div>
        )}
      </div>
    </div>
  );
}

export function LeadsCountBadge({ count, label = "leads" }) {
  return (
    <span className="inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-semibold text-[#007BFF]">
      {count} {label}
    </span>
  );
}

export function LeadsSelectionBar({ count, children }) {
  return (
    <div className="website-leads-table-selection">
      <span className="website-leads-table-selection__count">{count} selected</span>
      {children}
    </div>
  );
}

export function LeadsTableToolbar({ title, right, onSettingsClick }) {
  return (
    <div className="website-leads-table-toolbar">
      <h2
        className="mb-0 text-truncate"
        style={{ fontSize: "0.8125rem", fontWeight: 600, color: "#35314d" }}
      >
        {title}
      </h2>
      <div className="website-leads-table-toolbar__right">
        {right}
        {onSettingsClick && (
          <button
            type="button"
            className="website-leads-settings-btn"
            onClick={onSettingsClick}
            title="Column settings"
            aria-label="Column settings"
          >
            <FaCog size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

export function LeadsFilterCard({ children, actions, columns = 4, headerRight }) {
  const gridClass =
    columns === 3
      ? "website-leads-filter-grid website-leads-filter-grid--3"
      : "website-leads-filter-grid";

  return (
    <div className="mb-3 rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="website-leads-card-header">
        <h2 className="mb-0 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          Search &amp; Filters
        </h2>
        {headerRight && (
          <div className="website-leads-card-header__right">{headerRight}</div>
        )}
      </div>
      <div className={gridClass}>{children}</div>
      {actions && (
        <div className="website-leads-filter-actions">{actions}</div>
      )}
    </div>
  );
}

export function LeadsFilterField({ label, id, children, className = "" }) {
  return (
    <div className={`min-w-0 ${className}`.trim()}>
      <label
        htmlFor={id}
        className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

export const LEADS_FILTER_ICONS = {
  name: FaUser,
  email: FaEnvelope,
  phone: FaPhone,
  country: FaGlobe,
  destination: FaMapMarkerAlt,
  form_type: FaListAlt,
  assigned_user_id: FaUserCheck,
  date: FaCalendarAlt,
};

export function LeadsFilterInput({
  id,
  type = "text",
  icon: Icon,
  placeholder,
  value,
  onChange,
}) {
  const IconComponent = Icon || FaUser;

  return (
    <div className="website-leads-filter-input-wrap">
      <span className="website-leads-filter-input-icon" aria-hidden="true">
        <IconComponent size={11} />
      </span>
      <input
        type={type}
        id={id}
        className="website-leads-filter-input"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
      />
    </div>
  );
}

export function LeadsFilterSelect({
  id,
  icon: Icon,
  value,
  onChange,
  children,
  ariaLabel,
}) {
  const IconComponent = Icon || FaListAlt;

  return (
    <div className="website-leads-filter-input-wrap">
      <span className="website-leads-filter-input-icon" aria-hidden="true">
        <IconComponent size={11} />
      </span>
      <select
        id={id}
        className="website-leads-filter-input website-leads-filter-select"
        value={value}
        onChange={onChange}
        aria-label={ariaLabel}
      >
        {children}
      </select>
    </div>
  );
}

export function LeadsDateInput({ id, value, onChange }) {
  const inputRef = useRef(null);

  const openPicker = () => {
    const input = inputRef.current;
    if (!input) return;
    if (typeof input.showPicker === "function") {
      try {
        input.showPicker();
      } catch {
        input.focus();
      }
    } else {
      input.focus();
    }
  };

  return (
    <div
      className="website-leads-filter-input-wrap website-leads-date-input-wrap"
      onClick={openPicker}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openPicker();
        }
      }}
      role="presentation"
    >
      <span className="website-leads-filter-input-icon" aria-hidden="true">
        <FaCalendarAlt size={11} />
      </span>
      <input
        ref={inputRef}
        type="date"
        id={id}
        className="website-leads-filter-input website-leads-date-input"
        value={value}
        onChange={onChange}
        onClick={openPicker}
      />
    </div>
  );
}

export function LeadsDateFilterField({ label, id, value, onChange }) {
  return (
    <LeadsFilterField label={label} id={id}>
      <LeadsDateInput id={id} value={value} onChange={onChange} />
    </LeadsFilterField>
  );
}

export function LeadsDateRangeFilters({
  startId = "start_date",
  endId = "end_date",
  startValue = "",
  endValue = "",
  onChange,
}) {
  return (
    <>
      <LeadsDateFilterField
        label="Start Date"
        id={startId}
        value={startValue}
        onChange={(event) => onChange("start_date", event.target.value)}
      />
      <LeadsDateFilterField
        label="End Date"
        id={endId}
        value={endValue}
        onChange={(event) => onChange("end_date", event.target.value)}
      />
    </>
  );
}

export const leadsInputClass =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-800 outline-none transition focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/15";

export const leadsSelectClass = leadsInputClass;

export const leadsCellInputClass = "website-leads-cell-input";

export function LeadsTruncatedText({ text, empty = "—" }) {
  const value = text?.toString().trim() || "";
  const display = value || empty;

  return (
    <span className="website-leads-truncate" title={value || undefined}>
      {display}
    </span>
  );
}

export function LeadsMovedIcon({ title = "Moved to client" }) {
  return (
    <span
      className="website-leads-moved-icon website-leads-badge website-leads-badge--moved website-leads-badge--icon"
      title={title}
      aria-label={title}
    >
      <FaCheckCircle size={11} />
    </span>
  );
}

export function LeadsRowIconButton({
  icon: Icon,
  onClick,
  title,
  variant = "primary",
  disabled = false,
  type = "button",
}) {
  const variantClass = {
    primary: "border-primary-subtle bg-primary-subtle text-primary",
    success: "border-success-subtle bg-success-subtle text-success",
    danger: "border-danger-subtle bg-danger-subtle text-danger",
    secondary: "border-secondary-subtle bg-light text-secondary",
    muted: "border-secondary-subtle bg-white text-muted",
  }[variant] || "border-primary-subtle bg-primary-subtle text-primary";

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`website-leads-row-icon-btn d-inline-flex align-items-center justify-content-center rounded border ${variantClass}`}
      title={title}
      aria-label={title}
    >
      <Icon size={10} />
    </button>
  );
}

export function LeadsPrimaryButton({ children, onClick, disabled, type = "button" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
      style={{ backgroundColor: CMS_PRIMARY }}
    >
      {children}
    </button>
  );
}

export function LeadsGhostButton({ children, onClick, disabled, type = "button" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 disabled:opacity-40"
    >
      {children}
    </button>
  );
}

export function LeadsSuccessButton({ children, onClick, disabled, type = "button" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-40"
    >
      {children}
    </button>
  );
}

export function LeadsTableCard({ toolbar, children, loading, loader }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      {toolbar && (
        <div className="website-leads-table-toolbar-bar">{toolbar}</div>
      )}
      {loading ? (
        <div className="d-flex justify-content-center px-3 py-4">{loader}</div>
      ) : (
        children
      )}
    </div>
  );
}

export function LeadsTableWrap({ children }) {
  return <div className="website-leads-table-wrap">{children}</div>;
}

export const getLeadsTableColClass = (key) => {
  const map = {
    select: "wl-col-check",
    id: "wl-col-id",
    actions: "wl-col-actions",
    notes: "wl-col-notes",
    inquiry: "wl-col-inquiry",
    type: "wl-col-type",
    assigned_user_id: "wl-col-assigned",
    created_at: "wl-col-date",
    assigned_at: "wl-col-date",
  };
  return map[key] || "";
};

export function LeadsNotesInput({ value, onChange, onBlur, placeholder = "Note…" }) {
  return (
    <input
      type="text"
      className="website-leads-notes-input"
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
    />
  );
}

export function LeadsPagination({ pagination, onPageChange, onPerPageChange }) {
  return (
    <div className="website-leads-filter-actions mt-3 rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="d-flex flex-wrap align-items-center gap-2">
        <select
          value={pagination.perPage}
          onChange={onPerPageChange}
          className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs text-gray-700"
          aria-label="Rows per page"
        >
          <option value="10">10</option>
          <option value="25">25</option>
          <option value="100">100</option>
          <option value="200">200</option>
          <option value={pagination.total || 10}>All</option>
        </select>
        <span className="text-xs text-gray-500">
          Page {pagination.currentPage} · Showing{" "}
          {Math.min(pagination.perPage, pagination.total)} of {pagination.total}
        </span>
      </div>
      <ReactPaginate
        previousLabel="Prev"
        nextLabel="Next"
        breakLabel="…"
        pageCount={pagination.lastPage}
        marginPagesDisplayed={1}
        pageRangeDisplayed={3}
        onPageChange={onPageChange}
        forcePage={pagination.currentPage - 1}
        containerClassName="pagination mb-0 justify-content-end"
        pageClassName="page-item"
        pageLinkClassName="page-link"
        previousClassName="page-item"
        previousLinkClassName="page-link"
        nextClassName="page-item"
        nextLinkClassName="page-link"
        activeClassName="active"
        disabledClassName="disabled"
      />
    </div>
  );
}
